import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  type AppliedTheme,
  type ThemePref,
  readStoredPref,
  resolveTheme,
  storePref,
} from './theme';

interface ThemeContextValue {
  /** Preferência escolhida (light | dark | system). */
  pref: ThemePref;
  /** Tema efetivamente aplicado (system colapsado em light|dark). */
  applied: AppliedTheme;
  /** Define a preferência (persiste no localStorage). */
  setPref: (pref: ThemePref) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

const DARK_QUERY = '(prefers-color-scheme: dark)';

function systemPrefersDark(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia(DARK_QUERY).matches
    : false;
}

/** Aplica o tema no <html> (data-theme) para os tokens CSS reagirem. */
function applyToDocument(applied: AppliedTheme): void {
  if (typeof document !== 'undefined') {
    document.documentElement.dataset.theme = applied;
  }
}

/**
 * Provider de tema: resolve claro/escuro/sistema, persiste a preferência e
 * reage à mudança de `prefers-color-scheme` quando em modo "sistema".
 */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [pref, setPrefState] = useState<ThemePref>(() =>
    readStoredPref(typeof localStorage !== 'undefined' ? localStorage : null),
  );
  const [systemDark, setSystemDark] = useState<boolean>(systemPrefersDark);

  // Acompanha a preferência do SO enquanto estiver em modo "sistema".
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia(DARK_QUERY);
    const onChange = (e: MediaQueryListEvent) => setSystemDark(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const applied = useMemo(() => resolveTheme(pref, systemDark), [pref, systemDark]);

  useEffect(() => {
    applyToDocument(applied);
  }, [applied]);

  const setPref = useCallback((next: ThemePref) => {
    setPrefState(next);
    storePref(typeof localStorage !== 'undefined' ? localStorage : null, next);
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({ pref, applied, setPref }),
    [pref, applied, setPref],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

/** Hook de acesso ao tema. Lança se usado fora do ThemeProvider (erro de wiring). */
export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme deve ser usado dentro de <ThemeProvider>');
  return ctx;
}
